import React from "react";
import { InView } from "react-intersection-observer";
import { useSelector } from "react-redux";

import { trackCartImpression } from "@services/tracking";

import { useThunkDispatch } from "@redux/dispatcher";
import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { load } from "@redux/modules/api/data/cart/cart.module";
import { isLoadingCart } from "@redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";
import { TApiAction } from "@redux/store.types";

import { Spinner } from "@components/General/Spinner/spinner.component";
import { ShoppingCartSummary } from "@components/ShoppingCartSummary/shoppingCartSummary.component";

import CartSalesIncentives from "./cartSalesIncentives.component";
import EmptyCartContent from "./emptyCart.component";
import "./miniCart.scss";

export type MiniCartProps = {
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
    urlTarget?: string;
    shoppingCart?: CartModel | null;
    closeCallback?: () => void;
    isUpdatingCart?: { [key: string]: boolean };
    activeId: string;
};

const MiniCart = ({
    shoppingCart,
    urlTarget,
    closeCallback,
    removeFromCart,
    isUpdatingCart,
    activeId,
}: MiniCartProps) => {
    const { t } = useGranite();
    const dispatcher = useThunkDispatch();
    const cartLoading = useSelector(isLoadingCart);

    const onInView = (inView: boolean) => {
        if (inView) {
            if (shoppingCart) {
                trackCartImpression(shoppingCart, "cart quickview");
            } else {
                dispatcher(load()).then((action: TApiAction<CartModel>) => {
                    if (action.payload) {
                        trackCartImpression(action.payload, "cart quickview");
                    }
                });
            }
        }
    };

    const hasCartItems = (shoppingCart?.items || []).length > 0;

    return (
        <>
            {
                <InView triggerOnce={true} onChange={onInView}>
                    <div className="mt-3">
                        <div className="mini-cart small">
                            {cartLoading && (
                                <div className="text-center px-3">
                                    <Spinner color="primary" />
                                </div>
                            )}
                            {!cartLoading && !hasCartItems && (
                                <EmptyCartContent />
                            )}
                            <ShoppingCartSummary
                                productLinkingAvailable={true}
                                isMiniCart={true}
                                shoppingCart={shoppingCart}
                                removeFromCart={removeFromCart}
                                isUpdatingCart={isUpdatingCart}
                                activeId={activeId}
                            />
                            <section className="mini-cart__sales-incentives px-3">
                                <CartSalesIncentives
                                    incentives={shoppingCart?.salesIncentives}
                                />
                            </section>
                            <section className="mini-cart__button-holder mt-3 px-3">
                                <div className="d-flex">
                                    <a
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault();
                                            if (closeCallback) {
                                                closeCallback();
                                            }
                                        }}
                                        className="mini-cart__close-button align-self-center mr-auto"
                                    >
                                        <i className="las la-angle-left" />
                                        {t("general.miniCart.button.continue")}
                                    </a>
                                    {hasCartItems && (
                                        <a
                                            href={urlTarget}
                                            className="btn btn-success"
                                        >
                                            {t("general.miniCart.button.order")}
                                        </a>
                                    )}
                                </div>
                            </section>
                            <section className="mini-cart__promotionBannerText-holder small px-3" />
                        </div>
                    </div>
                </InView>
            }
        </>
    );
};

export { MiniCart };
